import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Pencil } from "lucide-react";
import {
  useUpdateContract,
  getGetContractQueryKey,
  getListContractsQueryKey,
  getListTermSheetVersionsQueryKey,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { handleApiError } from "@/lib/handleApiError";
import { randomId } from "@/lib/utils";

interface BonusLike {
  id?: number | string;
  type?: string | null;
  label?: string | null;
  customLabel?: string | null;
  amount?: number | null;
  paymentTiming?: string | null;
  isRecurring?: boolean | null;
  [key: string]: unknown;
}

interface CurrentTerms {
  salaryRangeMin?: number | null;
  salaryRangeMax?: number | null;
  signingBonus?: number | null;
  keyTerms?: string | null;
  bonuses?: BonusLike[];
}

interface ReviseTermSheetTermsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  termSheetId: number;
  current: CurrentTerms;
  onSaved?: () => void;
}

function applySigningBonus(bonuses: BonusLike[], amount: number | null): BonusLike[] {
  const arr = bonuses.map((b) => ({ ...b }));
  if (amount == null) return arr;
  const idx = arr.findIndex(
    (b) =>
      b.type === "signing" ||
      `${b.label ?? ""} ${b.customLabel ?? ""}`.toLowerCase().includes("signing"),
  );
  if (idx >= 0) {
    arr[idx] = { ...arr[idx], amount };
    return arr;
  }
  arr.push({
    id: randomId(),
    type: "signing",
    label: "Signing Bonus",
    customLabel: "Signing Bonus",
    amount,
    paymentTiming: "one_time_signing",
    isRecurring: false,
  });
  return arr;
}

/**
 * Admin path to update *our* headline offer after create / during negotiation.
 * Distinct from Record Counter (candidate proposal) and Accept Counter.
 */
export function ReviseTermSheetTermsDialog({
  open,
  onOpenChange,
  termSheetId,
  current,
  onSaved,
}: ReviseTermSheetTermsDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const updateContract = useUpdateContract();

  const [salaryRangeMin, setSalaryRangeMin] = useState("");
  const [salaryRangeMax, setSalaryRangeMax] = useState("");
  const [signingBonus, setSigningBonus] = useState("");
  const [keyTerms, setKeyTerms] = useState("");

  useEffect(() => {
    if (!open) return;
    setSalaryRangeMin(current.salaryRangeMin != null ? String(current.salaryRangeMin) : "");
    setSalaryRangeMax(current.salaryRangeMax != null ? String(current.salaryRangeMax) : "");
    setSigningBonus(current.signingBonus != null ? String(current.signingBonus) : "");
    setKeyTerms(current.keyTerms ?? "");
    // Only seed when the dialog opens — parent re-renders pass a new `current`
    // object each time and must not wipe in-progress edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional
  }, [open]);

  function parseNum(s: string): number | null {
    if (!s.trim()) return null;
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  }

  const handleSave = () => {
    const min = parseNum(salaryRangeMin);
    const max = parseNum(salaryRangeMax);
    const bonus = parseNum(signingBonus);
    if (min != null && max != null && min > max) {
      toast({
        title: "Invalid salary range",
        description: "Minimum cannot exceed maximum.",
        variant: "destructive",
      });
      return;
    }

    const nextBonuses = applySigningBonus(current.bonuses ?? [], bonus);

    updateContract.mutate(
      {
        id: termSheetId,
        data: {
          salaryRangeMin: min,
          salaryRangeMax: max,
          signingBonus: bonus,
          keyTerms: keyTerms.trim() || null,
          bonuses: nextBonuses as Parameters<typeof updateContract.mutate>[0]["data"]["bonuses"],
        } as Parameters<typeof updateContract.mutate>[0]["data"],
      },
      {
        onSuccess: () => {
          toast({
            title: "Terms updated",
            description: "Headline terms saved. Send revised terms when ready.",
          });
          queryClient.invalidateQueries({ queryKey: getGetContractQueryKey(termSheetId) });
          queryClient.invalidateQueries({ queryKey: getListContractsQueryKey() });
          queryClient.invalidateQueries({ queryKey: getListTermSheetVersionsQueryKey(termSheetId) });
          onOpenChange(false);
          onSaved?.();
        },
        onError: (err) => handleApiError(toast, err, { fallbackTitle: "Could not save terms" }),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="revise-term-sheet-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-5 w-5" />
            Revise terms
          </DialogTitle>
          <DialogDescription>
            Update our offer to reflect negotiation. This changes the live term sheet — it does not
            record a candidate counter. After saving, use Send for Agreement to share the revision.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="revise-salary-min">Salary range min</Label>
              <Input
                id="revise-salary-min"
                type="number"
                min={0}
                value={salaryRangeMin}
                onChange={(e) => setSalaryRangeMin(e.target.value)}
                data-testid="input-revise-salary-min"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="revise-salary-max">Salary range max</Label>
              <Input
                id="revise-salary-max"
                type="number"
                min={0}
                value={salaryRangeMax}
                onChange={(e) => setSalaryRangeMax(e.target.value)}
                data-testid="input-revise-salary-max"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="revise-signing-bonus">Signing bonus</Label>
            <Input
              id="revise-signing-bonus"
              type="number"
              min={0}
              value={signingBonus}
              onChange={(e) => setSigningBonus(e.target.value)}
              data-testid="input-revise-signing-bonus"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="revise-key-terms">Key terms</Label>
            <Textarea
              id="revise-key-terms"
              rows={5}
              value={keyTerms}
              onChange={(e) => setKeyTerms(e.target.value)}
              data-testid="textarea-revise-key-terms"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={updateContract.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={updateContract.isPending}
            data-testid="button-save-revised-terms"
          >
            {updateContract.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…
              </>
            ) : (
              "Save revised terms"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
